import classNames from "classnames";
import {
    resolveAnatomyBindings,
    type ResolvedAnatomyBinding,
} from "./componentAnatomy";
import { otpFieldAnatomy, otpFieldVariant } from "./componentAnatomyOTPField";
import {
    bindingReader,
    Part,
    type AnatomyPartHighlight,
} from "./componentAnatomyParts";
import otpFieldStyles from "./ComponentAnatomySchematicOTPField.module.scss";
import styles from "./ComponentAnatomyView.module.scss";

const restingBindings = resolveAnatomyBindings({
    spec: otpFieldAnatomy,
    variant: otpFieldVariant,
    stateId: "resting",
});

/**
 * Six magnified slots are wider than the plate, so it draws a slice of a
 * partial code: a filled slot, the active one, and an empty one after it.
 */
const slotDigits = ["1", "", ""];
const activeSlotIndex = 1;

const SlotSchematic = ({
    bindings,
    highlight,
    digit,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
    digit: string;
}) => {
    const read = bindingReader(bindings);
    const radius = read("border", "border-radius");
    const ringSpread = read("focusRing", "box-shadow (spread)");
    const ringColor = read("focusRing", "box-shadow (color)");

    return (
        <span
            className={otpFieldStyles.otpFieldSlot}
            style={{
                width: read("background", "width"),
                height: read("background", "height"),
            }}
        >
            {ringSpread !== undefined && ringColor !== undefined && (
                <Part
                    partId="focusRing"
                    highlight={highlight}
                    className={styles.buttonRing}
                    hitInset={`calc(-1 * ${ringSpread})`}
                    style={{
                        borderRadius: radius,
                        boxShadow: `0 0 0 ${ringSpread} ${ringColor}`,
                    }}
                />
            )}
            <Part
                partId="border"
                highlight={highlight}
                className={styles.buttonBorder}
                style={{
                    borderColor: read("border", "border-color"),
                    borderWidth: read("border", "border-width"),
                    borderRadius: radius,
                }}
            />
            <Part
                partId="background"
                highlight={highlight}
                className={styles.buttonBackground}
                style={{
                    backgroundColor: read("background", "background-color"),
                    borderRadius: radius,
                }}
            />
            <span
                className={styles.buttonContent}
                style={{ width: "100%", justifyContent: "center" }}
            >
                {digit !== "" && (
                    <Part
                        partId="label"
                        highlight={highlight}
                        style={{
                            color: read("label", "color"),
                            fontFamily: read("label", "font-family"),
                            fontSize: read("label", "font-size"),
                            fontWeight: read("label", "font-weight"),
                            lineHeight: read("label", "line-height"),
                            letterSpacing: read("label", "letter-spacing"),
                        }}
                    >
                        {digit}
                    </Part>
                )}
            </span>
        </span>
    );
};

/**
 * X-ray plate for OTPField. Hover and focus paint only the active slot, the way
 * the live field does; invalid and disabled paint every slot.
 */
export const OTPFieldSchematic = ({
    bindings,
    highlight,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
}) => {
    const isSlotLocalState = bindings.some(
        ({ setByStateId }) =>
            setByStateId === "hover" || setByStateId === "focus",
    );

    return (
        <span
            className={classNames(
                styles.schematic,
                otpFieldStyles.otpFieldSchematic,
            )}
            style={{ gap: bindingReader(bindings)("surface", "gap") }}
        >
            <Part
                partId="surface"
                highlight={highlight}
                style={{ position: "absolute", inset: 0 }}
            />
            {slotDigits.map((digit, index) => (
                <SlotSchematic
                    key={index}
                    bindings={
                        isSlotLocalState && index !== activeSlotIndex
                            ? restingBindings
                            : bindings
                    }
                    highlight={highlight}
                    digit={digit}
                />
            ))}
        </span>
    );
};
